// @givmo/board — the disclosures linked to one motion, shown before the board votes.
//
// ══ THIS BLOCK PROMPTS. IT NEVER BLOCKS, NEVER RECUSES, AND NEVER CONCLUDES ════════════════════
//
// It is the whole point of the conflict slice reaching the in-meeting record: a disclosure recorded
// weeks ago, and an officer's recorded decision that it names THIS motion, put in front of the board
// while the vote is still to come. What it must not become is the module deciding anything.
//
// Four rails, and each one is a thing this file deliberately does not do:
//
//   1. **NO GATE.** There is no disabled vote button, no confirmation step, no "resolve this first".
//      Whether the board votes, and who takes part, is the board's own act. A UI that withheld the
//      vote affordance would be the product deciding that a recorded disclosure means someone may not
//      vote — which is a legal conclusion, and one the module has no basis for.
//   2. **NO RECUSAL IS OFFERED FROM HERE, AND NONE IS IMPLIED.** Recording a recusal stays where
//      CBG-RC3 put it (the motion's own "Record a recusal" action, beside the board's disinterested-
//      director determination). Putting a recusal button inside a conflict block would read as the
//      module's recommendation of what to do about the thing it just showed you.
//   3. **NO SENTENCE ABOUT RECUSAL IS WRITTEN HERE.** Every link carries the backend's own `statement`,
//      which already ends with the recusal clause as a recorded fact and its date — or, where the book
//      holds none, as a statement that it holds none. Rendering that verbatim is what keeps "no recusal
//      has been recorded" from drifting into "no recusal was needed" (or its opposite). The structured
//      fields beside it are DATA — a name, a counterparty, a date — never a second sentence.
//   4. **NO ARITHMETIC, AND NO COUNT.** Nothing here is added up, and nothing here reaches the quorum
//      figures. A recused director still counts toward quorum (DGCL § 144(d)(1)); that arithmetic is
//      the server's, and a conflict surface is exactly where a future author is tempted to subtract a
//      seat.
//
// ⚠ **EMPTY IS NOT A FINDING, so empty renders NOTHING.** No "no conflicts" line, no green tick, no
// "nothing to disclose". An absent block means the officer has recorded no link for this motion — it
// says nothing about whether there is something to disclose, and any affirmative rendering of that
// emptiness would be the module asserting a clean bill it has no basis for. This mirrors the
// suggestion read's method statement exactly: the risky reading of nothing is that something was
// checked and found clear.
//
// The links arrive on the motion already narrowed to CONFIRMATIONS and already scoped to what this
// reader may see (`board_minutes_service._conflict_links_out`). Nothing is filtered again here — see
// `conflicts-decode.ts`.

import { Badge } from '@givmo/design-system';
import { displayDate } from '../../lib/display';
import { displayInstantInZone } from '../meetings/zoned-time';
import {
  linkTargetLabel,
  recordedViaLabel,
  type ConflictLinkRow,
} from '../conflicts/conflicts-decode';

export interface PreVoteConflictBlockProps {
  links: readonly ConflictLinkRow[];
  /** The MEETING's zone — every instant below is the officer's act at the meeting, not the reader's day. */
  timeZone: string | null;
  testId: string;
}

export function PreVoteConflictBlock({ links, timeZone, testId }: PreVoteConflictBlockProps) {
  if (links.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-8 rounded-md border border-border-subtle bg-surface-sunken p-12"
      data-testid={testId}
    >
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
        Disclosures you linked to this motion
      </span>

      <p className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
        {/* The one framing line, and it hands the decision back to the board in as many words. It says
            what the board DOES with a disclosure — records a recusal — without saying that this one
            calls for it. */}
        These are disclosures recorded in your book that you have recorded as naming this motion.
        What follows from them is the board’s own decision, and where the board decides a director
        stands out of a vote, that is recorded as a recusal.
      </p>

      <ul className="flex flex-col gap-10">
        {links.map((link) => (
          <li
            key={link.id}
            className="flex flex-col gap-6 border-t border-border-subtle pt-10 first:border-t-0 first:pt-0"
            data-testid={`${testId}-${link.id}`}
          >
            {/* ⚠ THE BACKEND'S SENTENCE, VERBATIM. It names the disclosure, its date, the officer's
                linking act and its date, and whether a recusal for that seat has been recorded on this
                motion. Every one of those is a recorded fact; none of them is a conclusion. Nothing in
                this app composes, trims, re-orders or re-words it. */}
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-strong"
              data-testid={`${testId}-statement-${link.id}`}
            >
              {link.statement}
            </p>

            <div className="flex flex-wrap items-center gap-6">
              <Badge tone="neutral">{linkTargetLabel(link.link_target)}</Badge>
              {link.disclosing_member_name !== null ? (
                <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
                  {link.disclosing_member_name}
                </span>
              ) : null}
              {link.counterparty_name !== null ? (
                <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
                  · {link.counterparty_name}
                </span>
              ) : null}
              {link.disclosed_on !== null ? (
                <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                  · disclosed {displayDate(link.disclosed_on)}
                </span>
              ) : null}
            </div>

            <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {/* The officer's act, dated in the MEETING's zone — a link recorded in the room after
                  ~8pm local is the next UTC day, and dating it that way puts the officer's act after
                  the meeting it happened at. */}
              {recordedViaLabel(link.recorded_via)} ·{' '}
              {displayInstantInZone(link.recorded_at, timeZone)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
